'use client';
import React, { useState, useEffect } from 'react';
import { getProjectsDB, getHutangListDB, getPiutangListDB, getPembayaranListDB } from '@/lib/supabaseData';
import CurrencyDisplay from '@/components/ui/CurrencyDisplay';
import Link from 'next/link';

interface RekapRow {
  id: string;
  projectId: string;
  nama: string;
  budget: number;
  hutang: number;
  piutang: number;
  netDebt: number;
  pctBudget: number;
}

export default function RekapProyekTable() {
  const [rekapData, setRekapData] = useState<RekapRow[]>([]);
  const [totals, setTotals] = useState({ budget: 0, hutang: 0, piutang: 0, netDebt: 0, pctBudget: 0 });

  useEffect(() => {
    Promise.all([getProjectsDB(), getHutangListDB(), getPiutangListDB(), getPembayaranListDB()]).then(([projects, hutangList, piutangList, pembayaranList]) => {
      const activeStatuses = ['Berjalan', 'Persiapan'];
      const activeProjects = projects.filter((p) => activeStatuses.includes(p.status));

      const dibayarMap: Record<string, number> = {};
      pembayaranList.forEach((p) => {
        dibayarMap[p.hutangId] = (dibayarMap[p.hutangId] ?? 0) + p.nominal;
      });

      const rows: RekapRow[] = activeProjects.map((project) => {
        const hutang = hutangList
          .filter((h) => h.projectId === project.id)
          .reduce((sum, h) => {
            const actualDibayar = dibayarMap[h.id] ?? 0;
            return sum + Math.max(0, h.nilaiTagihan - actualDibayar);
          }, 0);
        const piutang = piutangList.filter((p) => p.projectId === project.id).reduce((sum, p) => sum + p.sisaPiutang, 0);
        const netDebt = hutang - piutang;
        const pctBudget = project.budget > 0 ? (netDebt / project.budget) * 100 : 0;
        return { id: `rekap-${project.id}`, projectId: project.id, nama: project.nama, budget: project.budget, hutang, piutang, netDebt, pctBudget };
      });

      const totalBudget = rows.reduce((s, r) => s + r.budget, 0);
      const totalHutang = rows.reduce((s, r) => s + r.hutang, 0);
      const totalPiutang = rows.reduce((s, r) => s + r.piutang, 0);
      const totalNetDebt = totalHutang - totalPiutang;
      const totalPct = totalBudget > 0 ? (totalNetDebt / totalBudget) * 100 : 0;

      setRekapData(rows);
      setTotals({ budget: totalBudget, hutang: totalHutang, piutang: totalPiutang, netDebt: totalNetDebt, pctBudget: totalPct });
    });
  }, []);

  return (
    <div className="bg-white rounded-2xl overflow-hidden" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04)' }}>
      <div className="px-6 py-4 flex items-center justify-between border-b border-gray-50">
        <div>
          <h2 className="text-base font-700 text-gray-800">Rekap Keuangan Per Proyek</h2>
          <p className="text-xs text-gray-400 mt-0.5">Ringkasan hutang dan piutang per proyek aktif</p>
        </div>
        <Link href="/laporan" className="text-sm font-600 text-[#1a3a6b] hover:underline flex items-center gap-1">
          Lihat Semua →
        </Link>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" style={{ minWidth: '700px' }}>
          <thead>
            <tr className="bg-gray-50/80">
              <th className="text-left px-5 py-3.5 text-xs font-600 text-gray-400 uppercase tracking-wide whitespace-nowrap w-24">No.</th>
              <th className="text-left px-5 py-3.5 text-xs font-600 text-gray-400 uppercase tracking-wide whitespace-nowrap">Nama Proyek</th>
              <th className="text-right px-5 py-3.5 text-xs font-600 text-gray-400 uppercase tracking-wide whitespace-nowrap w-40">Budget</th>
              <th className="text-right px-5 py-3.5 text-xs font-600 text-gray-400 uppercase tracking-wide whitespace-nowrap w-36">Hutang</th>
              <th className="text-right px-5 py-3.5 text-xs font-600 text-gray-400 uppercase tracking-wide whitespace-nowrap w-36">Piutang</th>
              <th className="text-right px-5 py-3.5 text-xs font-600 text-gray-400 uppercase tracking-wide whitespace-nowrap w-36">Net Debt</th>
              <th className="text-right px-5 py-3.5 text-xs font-600 text-gray-400 uppercase tracking-wide whitespace-nowrap w-20">% Budget</th>
            </tr>
          </thead>
          <tbody>
            {rekapData?.map((row, idx) => (
              <tr
                key={row?.id}
                className="border-t border-gray-50 hover:bg-blue-50/30 transition-colors duration-100"
              >
                <td className="px-5 py-3.5 font-600 text-[#1a3a6b] text-xs whitespace-nowrap">{row?.projectId}</td>
                <td className="px-5 py-3.5 font-500 text-gray-700 whitespace-nowrap max-w-[180px] truncate">{row?.nama}</td>
                <td className="px-5 py-3.5 text-right tabular-nums text-gray-700 w-40">
                  <CurrencyDisplay value={row?.budget} className="w-full" />
                </td>
                <td className="px-5 py-3.5 text-right tabular-nums w-36">
                  <CurrencyDisplay value={row?.hutang} className="text-red-500 font-600 w-full" />
                </td>
                <td className="px-5 py-3.5 text-right tabular-nums w-36">
                  <CurrencyDisplay value={row?.piutang} className="text-teal-600 font-600 w-full" />
                </td>
                <td className="px-5 py-3.5 text-right tabular-nums w-36">
                  <CurrencyDisplay value={row?.netDebt} className={`font-700 w-full ${row?.netDebt <= 0 ? 'text-teal-600' : 'text-red-500'}`} />
                </td>
                <td className={`px-5 py-3.5 text-right tabular-nums font-600 whitespace-nowrap w-20 ${row?.pctBudget < 0 ? 'text-teal-600' : row?.pctBudget > 0 ? 'text-red-500' : 'text-gray-400'}`}>
                  {row?.pctBudget?.toFixed(1)}%
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-gray-50/80 border-t border-gray-100">
              <td className="px-5 py-3.5 font-700 text-xs text-gray-700" colSpan={2}>TOTAL</td>
              <td className="px-5 py-3.5 text-right tabular-nums w-40">
                <CurrencyDisplay value={totals.budget} className="font-700 text-gray-800 w-full" />
              </td>
              <td className="px-5 py-3.5 text-right tabular-nums w-36">
                <CurrencyDisplay value={totals.hutang} className="font-700 text-red-500 w-full" />
              </td>
              <td className="px-5 py-3.5 text-right tabular-nums w-36">
                <CurrencyDisplay value={totals.piutang} className="font-700 text-teal-600 w-full" />
              </td>
              <td className="px-5 py-3.5 text-right tabular-nums w-36">
                <CurrencyDisplay value={totals.netDebt} className={`font-700 w-full ${totals.netDebt <= 0 ? 'text-teal-600' : 'text-red-500'}`} />
              </td>
              <td className={`px-5 py-3.5 text-right tabular-nums font-700 whitespace-nowrap w-20 ${totals.pctBudget < 0 ? 'text-teal-600' : totals.pctBudget > 0 ? 'text-red-500' : 'text-gray-400'}`}>{totals.pctBudget?.toFixed(2)}%</td>
            </tr>
          </tfoot>
        </table>
      </div>
      <div className="px-6 py-3 border-t border-gray-50 text-center">
        <p className="text-xs text-gray-400">© 2025 Pesantren Ibnu Syam. All rights reserved.</p>
      </div>
    </div>
  );
}